import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PurchaseOrder } from '@/lib/schemas';

interface UsePurchaseOrdersOptions {
  page?: number;
  limit?: number;
}

export function usePurchaseOrders(options: UsePurchaseOrdersOptions = {}) {
  const { page = 1, limit = 10 } = options;

  return useQuery<{data: PurchaseOrder[], totalCount: number}, Error>({
    queryKey: ['purchase_orders', page, limit],
    queryFn: () => api.getPurchaseOrders({ page, limit }),
    staleTime: 0,
    gcTime: 0,
  });
}

export function usePurchaseOrder(id: string) {
  return useQuery<PurchaseOrder | null, Error>({
    queryKey: ['purchase_orders', id],
    queryFn: () => api.getPurchaseOrder(id),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation<PurchaseOrder, Error, Omit<PurchaseOrder, 'po_id' | 'po_number' | 'created_at' | 'updated_at'>>({
    mutationFn: (data) => api.createPurchaseOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase_orders'] });
    },
  });
}

export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation<PurchaseOrder | null, Error, { id: string; data: Partial<Omit<PurchaseOrder, 'po_id' | 'po_number' | 'created_at'>>}>({
    mutationFn: ({ id, data }) => api.updatePurchaseOrder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase_orders'] });
    },
  });
}

export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, string>({
    mutationFn: (id) => api.deletePurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase_orders'] });
    },
  });
}

export function useReceivePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation<PurchaseOrder | null, Error, { id: string; receiveData: { items: Array<{ po_item_id: string; quantity_received: number; batch_number?: string; expiry_date?: string }> } }>({
    mutationFn: ({ id, receiveData }) => api.receivePurchaseOrder(id, receiveData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase_orders'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
